import { useState, useEffect, useCallback, useMemo } from "react";
import type {
  ImpactLevel,
  OrganizationSize,
  ProbabilityLevel,
  SectorAnalysis,
} from "@/types/risk-analysis";
import {
  applyProbabilityChange,
  applyImpactChange,
  buildUpdatedAnalysis,
} from "@/utils/risk-wizard/matrixOperations";
import { compareSectorAnalysis } from "@/utils/risk-diff";

type UseSectorMatrixLocalStateProps = {
  sectorId: string;
  serverAnalysis: SectorAnalysis | null;
  defaultAnalysis: SectorAnalysis | null;
  organizationSize: OrganizationSize | null;
  initialLocked: boolean;
};

type UseSectorMatrixLocalStateReturn = {
  localAnalysis: SectorAnalysis | null;
  hasLocalChanges: boolean;
  isDefault: boolean;
  isLocked: boolean;
  setIsLocked: (locked: boolean) => void;
  handleProbabilityChange: (
    categoryId: string,
    actorId: string,
    probability: ProbabilityLevel
  ) => void;
  handleImpactChange: (categoryId: string, impact: ImpactLevel) => void;
  resetToDefault: () => void;
  applyExternalAnalysis: (analysis: SectorAnalysis) => void;
  getAnalysisForSave: () => SectorAnalysis | null;
};

export function useSectorMatrixLocalState({
  sectorId,
  serverAnalysis,
  defaultAnalysis,
  organizationSize,
  initialLocked,
}: UseSectorMatrixLocalStateProps): UseSectorMatrixLocalStateReturn {
  const [localAnalysis, setLocalAnalysis] = useState<SectorAnalysis | null>(
    serverAnalysis
  );
  const [isLocked, setIsLocked] = useState(initialLocked);

  const hasLocalChanges =
    localAnalysis && serverAnalysis
      ? compareSectorAnalysis(serverAnalysis, localAnalysis).hasChanges
      : false;

  useEffect(() => {
    setLocalAnalysis(serverAnalysis);
    setIsLocked(initialLocked);
  }, [sectorId, serverAnalysis, initialLocked]);

  const isDefault = useMemo(() => {
    if (!localAnalysis || !defaultAnalysis) return true;
    return (
      JSON.stringify(localAnalysis.attackMatrix) ===
      JSON.stringify(defaultAnalysis.attackMatrix)
    );
  }, [localAnalysis, defaultAnalysis]);

  useEffect(() => {
    if (!hasLocalChanges) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [hasLocalChanges]);

  const handleProbabilityChange = useCallback(
    (categoryId: string, actorId: string, probability: ProbabilityLevel) => {
      if (!localAnalysis || !organizationSize) return;

      const updatedMatrix = applyProbabilityChange(
        localAnalysis.attackMatrix,
        categoryId,
        actorId,
        probability,
        organizationSize
      );
      setLocalAnalysis(buildUpdatedAnalysis(localAnalysis, updatedMatrix));
    },
    [localAnalysis, organizationSize]
  );

  const handleImpactChange = useCallback(
    (categoryId: string, impact: ImpactLevel) => {
      if (!localAnalysis || !organizationSize) return;

      const updatedMatrix = applyImpactChange(
        localAnalysis.attackMatrix,
        categoryId,
        impact,
        organizationSize
      );
      setLocalAnalysis(buildUpdatedAnalysis(localAnalysis, updatedMatrix));
    },
    [localAnalysis, organizationSize]
  );

  const resetToDefault = useCallback(() => {
    if (!defaultAnalysis) return;
    setLocalAnalysis(defaultAnalysis);
    setIsLocked(true);
  }, [defaultAnalysis]);

  const applyExternalAnalysis = useCallback((analysis: SectorAnalysis) => {
    setLocalAnalysis(analysis);
  }, []);

  const getAnalysisForSave = useCallback(() => localAnalysis, [localAnalysis]);

  return {
    localAnalysis,
    hasLocalChanges,
    isDefault,
    isLocked,
    setIsLocked,
    handleProbabilityChange,
    handleImpactChange,
    resetToDefault,
    applyExternalAnalysis,
    getAnalysisForSave,
  };
}
